import type { TokenTotals } from '@/lib/api/generated/model';

import {
  CompositionBar,
  type CompositionSegment,
} from '@/shared/components/CompositionBar';
import { SectionHeading } from '@/shared/components/SectionHeading';
import { cacheShare } from '../cacheShare';
import { formatTokens } from '../money';
import { USAGE_CARD, USAGE_CARD_GAP } from '../usageLayout';

export interface TokenCompositionProps {
  totals: TokenTotals;
  /** The period's runs, said beside the total (spec 219 — it was a card of its own). */
  runCount?: number;
  /**
   * The bar and the tiles alone, for the meter's Token details (spec 232): no card and
   * no heading, because the meter's footer already says the total and the runs.
   */
  bare?: boolean;
}

/**
 * The tile's figure, at spec 226's `FigureRow` card size (spec 231 FR-006) so the
 * Token use tiles and the Models tab's in-card figures measure alike. The tiles keep
 * their own `<dl>` rather than calling `FigureRow`: the swatch has to sit beside the
 * word it keys, and the tiles sit on a muted surface in a grid, which the row does
 * not draw. The size is the shared thing; the classes are the row's.
 */
const TILE_FIGURE = 'text-xl leading-none font-semibold tabular-nums';

/**
 * Where the period's tokens went (spec 076 US2).
 *
 * This split is in every usage response and was shown on no surface at all — the largest
 * gap in the product between what it knows and what it says. It is also the clearest
 * answer to "why did this cost what it did", because the four categories are not priced
 * alike: a cache *read* is the cheap one and a cache *creation* is not, and telling them
 * apart is exactly what changes what a person does next with their prompts.
 *
 * **The fills are neutral (spec 251).** Input and output, the work a run is billed for,
 * are brand blue at two strengths; cache read and cache written are two greys. The four
 * were the chart tokens once, which read as success, info and warning, so 18% cache
 * written looked like a problem when it is only a kind of token. No status colour here.
 *
 * Every segment is labelled regardless, so no reader depends on telling the fills apart
 * (FR-009), and the cache share is said in words above the bar.
 */
export function TokenComposition({
  totals,
  runCount = 0,
  bare = false,
}: TokenCompositionProps) {
  const segments: CompositionSegment[] = [
    {
      key: 'input',
      label: 'input',
      fill: 'bg-chart-1',
      value: totals.input_tokens,
      display: formatTokens(totals.input_tokens),
    },
    {
      key: 'output',
      label: 'output',
      fill: 'bg-chart-1/55',
      value: totals.output_tokens,
      display: formatTokens(totals.output_tokens),
    },
    {
      key: 'cache-read',
      label: 'cache read',
      fill: 'bg-muted-foreground/60',
      value: totals.cache_read_tokens,
      display: formatTokens(totals.cache_read_tokens),
    },
    {
      key: 'cache-creation',
      label: 'cache written',
      fill: 'bg-muted-foreground/30',
      value: totals.cache_creation_tokens,
      display: formatTokens(totals.cache_creation_tokens),
    },
  ];

  if (totals.total_tokens === 0) return null;

  const cacheLine = cacheShare(totals);

  const figure = (
    <>
      {cacheLine === null ? null : (
        <p className="text-muted-foreground text-sm">{cacheLine}</p>
      )}
      {/*
        Every category is passed, including any at zero (FR-010). The bar draws only
        the non-zero ones — a zero-width band says nothing — but the legend keeps them
        all, because a workspace doing no caching and a build that does not report
        caching must not look alike. That is the same distinction spec 074 introduced
        `QUIET_FIGURE` to preserve, in a different place.
      */}
      <CompositionBar
        segments={segments}
        total={totals.total_tokens}
        description={describe(segments, totals.total_tokens)}
        legend={false}
      />
      {/*
        Each part as a figure of its own (spec 219), its swatch beside its words so the
        colour never carries the meaning alone. Every part is listed, zeros included
        (FR-010), for the reason the bar's comment gives.
      */}
      <dl aria-hidden="true" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className="bg-muted/40 flex min-w-0 flex-col gap-1 rounded-md px-3 py-2"
          >
            <dt className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <span
                className={`size-2 shrink-0 rounded-full ${segment.fill}`}
              />
              {segment.label}
            </dt>
            <dd className="flex min-w-0 flex-col gap-1">
              <span className={TILE_FIGURE}>{segment.display}</span>
              <span className="text-muted-foreground text-xs tabular-nums">
                {`${String(Math.round((segment.value / totals.total_tokens) * 100))}%`}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </>
  );

  if (bare)
    return <div className={`flex flex-col ${USAGE_CARD_GAP}`}>{figure}</div>;

  return (
    <section
      className={`${USAGE_CARD} flex flex-col ${USAGE_CARD_GAP}`}
      aria-labelledby="usage-tokens-heading"
    >
      {/*
        The period's token total and its runs on the heading row (spec 219, spec 231).
        They were a separate card at the foot of the breakdowns, which repeated this
        total and the headline's run count. Under the label on a phone.
      */}
      <SectionHeading
        as="h2"
        id="usage-tokens-heading"
        className="flex-wrap"
        action={
          <span className="text-muted-foreground text-xs tabular-nums max-sm:basis-full">
            {`${formatTokens(totals.total_tokens)} tokens across ${runCount === 1 ? '1 run' : `${String(runCount)} runs`}`}
          </span>
        }
      >
        Token use
      </SectionHeading>
      {figure}
    </section>
  );
}

/** The figure as a sentence, for anyone who cannot see it. */
function describe(segments: CompositionSegment[], total: number): string {
  const parts = segments
    .filter((segment) => segment.value > 0)
    .map((segment) => `${formatTokens(segment.value)} ${segment.label}`);
  return `Of ${formatTokens(total)} tokens: ${parts.join(', ')}.`;
}
